<template>
  <q-dialog v-model="shown">
    <q-card style="width: 400px;">
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          Edit {{ title }}
        </div>
        <q-space />
        <q-btn
          class="q-ma-xs"
          size="sm"
          outline
          :label="internalOrdering ? ('Add ' + title) : ('Done Adding')"
          @click="internalOrdering = !internalOrdering"
        />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <q-input
          v-if="!internalOrdering"
          v-model.trim="searchText"
          :placeholder="`Search ${title}...`"
          dense
          debounce="200"
          style="margin-bottom: 15px"
          clearable
        >
          <template #append>
            <q-icon name="search" />
          </template>
        </q-input>
        <transition-group
          v-if="!internalOrdering"
          name="list-complete"
        >
          <div
            v-for="item in combinedList"
            :key="item.id || item.value || item.key || item"
            style="margin-bottom: -1px"
            class="list-complete-item"
          >
            <q-item
              v-if="item.isSeparator"
              class="q-py-sm text-uppercase bg-grey-2 text-subtitle2"
              dense
              style="border: solid 1px #CCC;"
            >
              {{ item.label }}
            </q-item>
            <q-item
              v-else
              style="border: solid 1px #CCC;"
            >
              <slot :item="item">
                <q-item-section>
                  <q-item-label>
                    {{ item.label || $_.startCase(item.value || item) }}
                  </q-item-label>
                  <q-item-label
                    v-if="item.definition"
                    caption
                  >
                    {{ item.definition }}
                  </q-item-label>
                </q-item-section>
              </slot>
              <q-item-section
                v-if="!item.isSeparator"
                class="col-auto"
                style="border: solid 1px #ddd;"
              >
                <q-checkbox
                  :key="item.key"
                  :model-value="$_.includes(internalList, item.value || item)"
                  label="Include"
                  class="q-mr-md q-ml-xs"
                  @update:model-value="toggle(item)"
                />
              </q-item-section>
            </q-item>
          </div>
        </transition-group>
        <Container
          v-else
          @drop="onDrop"
        >
          <Draggable
            v-for="item in internalListWithDefinitions"
            :key="item.id || item.value || item.key || item"
            style="margin-bottom: -1px"
          >
            <q-item style="border: solid 1px #CCC;">
              <q-item-section avatar>
                <q-icon name="menu" />
              </q-item-section>
              <slot :item="item">
                <q-item-section>
                  <q-item-label>
                    {{ item.label || $_.startCase(item.value || item) }}
                  </q-item-label>
                  <q-item-label
                    v-if="item.definition"
                    caption
                  >
                    {{ item.definition }}
                  </q-item-label>
                </q-item-section>
              </slot>
              <q-item-section
                class="col-auto"
                style="border: solid 1px #ddd;"
              >
                <q-checkbox
                  :key="item.key"
                  :model-value="$_.includes(internalList, item.value || item)"
                  label="Include"
                  class="q-mr-md q-ml-xs"
                  @update:model-value="toggle(item)"
                />
              </q-item-section>
            </q-item>
          </Draggable>
        </Container>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>

import { getOptionsWithSeparators } from 'src/modules/admin/report/ReportUtils'
import { Container, Draggable } from 'vue3-smooth-dnd'

export default {
  name: 'ReportDataDisplayDialog',
  components: {
    Container,
    Draggable
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Values'
    },
    modelValue: {
      type: Boolean,
      required: false
    },
    list: {
      type: [Array, String],
      required: false,
      default () {
        return []
      }
    },
    options: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    ordering: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'list-updated'],
  data () {
    return {
      shown: false,
      internalList: this.list || [],
      internalOrdering: this.ordering,
      searchText: ''
    }
  },
  computed: {
    internalListWithDefinitions () {
      const itemsWithDefs = getOptionsWithSeparators(this.internalList, this.columns)
        .filter(item => !item.isSeparator)
      const itemMap = {}
      _.forEach(itemsWithDefs, item => {
        itemMap[item.value] = {
          value: item.value,
          label: item.label,
          definition: item.definition,
          category: item.category,
          key: item.key,
          isSeparator: false
        }
      })
      return _.map(this.internalList, value => {
        return itemMap[value] || { value, label: _.startCase(value) }
      })
    },
    combinedList () {
      const allItems = _.union(this.options, this.internalList)
      return getOptionsWithSeparators(allItems, this.columns, this.searchText)
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.internalOrdering = this.ordering
      this.$emit('update:model-value', this.shown)
    },
    internalList: {
      handler: function () {
        this.$emit('list-updated', this.internalList)
      },
      deep: true
    },
    list () {
      if (_.isEqual(this.list, this.internalList)) return

      this.internalList = this.list
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    toggle (item) {
      const valueToToggle = item.value || item
      if (!valueToToggle) {
        return
      }
      if (_.includes(this.internalList, valueToToggle)) {
        this.internalList = _.without(this.internalList, valueToToggle)
      } else {
        this.internalList.push(valueToToggle)
      }
    },
    onDrop (dropResult) {
      this.internalList = this.$mh.applyDrag(this.internalList, dropResult)
    }
  }
}

</script>

<style lang="scss" scoped>
.list-complete-item {
  transition: all 0.5s;
}
.list-complete-enter {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-to {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-active {
  position: absolute;
}
</style>
